import { ENDPOINT_AI } from '../config'
import type {
  AiReindexResponse,
  AiSourceTypeFilter,
} from '../types'
import AuthAxios from '@/lib/api/axios/AuthAxios'

export interface PostReindexRequest {
  /**
   * When true, the backfill writes `UpsertForce` outbox rows — the indexer
   * bypasses its content-hash short-circuit and re-embeds every chunk. Use
   * after a template change makes existing chunk text stale.
   */
  force?: boolean
  /** Optional scope to a single source type. Omitted = both. */
  sourceType?: AiSourceTypeFilter
  /**
   * When true, override the 409 over-quota gate. Per-chunk AiSpendGate still
   * enforces the hard ceiling during embedding generation regardless — so an
   * over-confirmed reindex may stall mid-flight with outbox retries.
   */
  confirmCost?: boolean
}

/**
 * Confirm-path reindex. Enqueues a Hangfire backfill job and persists a
 * ReindexJob row so the Jobs panel can surface progress and enqueue-failure
 * orphans. Use `postReindexEstimate` first if the UI needs to render the
 * pre-flight cost-confirm gate.
 *
 * Returns 202 with the estimate + queued job IDs on success.
 *
 * Throws (via AuthAxios) on the 409 over-quota path; callers should catch
 * and read `error.response.data` as `AiReindexQuotaExceededError` to render
 * the cost-confirm dialog.
 */
export async function postReindex(
  workspaceSlug: string,
  request: PostReindexRequest = {},
): Promise<AiReindexResponse> {
  const params = buildReindexParams(request)
  const response = await AuthAxios.post<AiReindexResponse>(
    `${ENDPOINT_AI(workspaceSlug)}/index`,
    null,
    { params },
  )
  return response.data
}

export function buildReindexParams(
  request: PostReindexRequest & { dryRun?: boolean },
): Record<string, string> {
  const params: Record<string, string> = {}
  if (request.force) params.force = 'true'
  if (request.sourceType) params.sourceType = request.sourceType
  if (request.confirmCost) params.confirmCost = 'true'
  if (request.dryRun) params.dryRun = 'true'
  return params
}
